import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { MessageSquare, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact SMS-usage overview card for the shop admin Dashboard.
 *
 * Copy: "20 credits used of 300"   [Top up SMS credits →]
 *       — with a progress bar that turns amber at 70% and red at 90%.
 *
 * Data source: `GET /api/sms/usage`. Bundle credits (top-ups the shop
 * has already bought) fold into the "of 300" number so a fresh top-up
 * bumps the visible cap immediately.
 */
export const SmsUsageCard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;
        const res = await fetch(`${BACKEND_URL}/api/sms/usage`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        const j = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
        setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="panel" data-testid="sms-usage-card-error" style={{ padding: 14 }}>
        <span style={{ color: 'var(--red)', fontSize: 12 }}>SMS usage unavailable: {error}</span>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="panel" data-testid="sms-usage-card-loading" style={{ padding: 14, color: 'var(--mute)', fontSize: 13 }}>
        Loading SMS usage…
      </div>
    );
  }

  const capTotal = data.effective_cap;
  const pctOfEffective = capTotal > 0 ? Math.min(100, Math.round((data.used / capTotal) * 100)) : 0;
  const bar =
    pctOfEffective >= 90 ? '#dc2626' :
    pctOfEffective >= 70 ? '#d97706' : '#059669';

  return (
    <div className="panel" data-testid="sms-usage-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8, background: bar + '18',
          display: 'grid', placeItems: 'center', color: bar, flexShrink: 0,
        }}>
          <MessageSquare size={16} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--mute)', letterSpacing: 0.3, textTransform: 'uppercase' }}>
            SMS this month
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }} data-testid="sms-usage-text">
            <span style={{ color: bar }}>{data.used}</span>
            {' '}credits used of{' '}
            {capTotal > 0 ? capTotal : '∞'}
            {data.bundle_credit > 0 && (
              <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--mute)', marginLeft: 6 }}>
                (incl. {data.bundle_credit} bundle)
              </span>
            )}
          </div>
          {capTotal > 0 && (
            <div
              aria-hidden
              style={{
                marginTop: 8,
                height: 6,
                width: '100%',
                background: 'var(--line, #e5e7eb)',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <div
                data-testid="sms-usage-bar"
                style={{
                  height: '100%',
                  width: `${pctOfEffective}%`,
                  background: bar,
                  transition: 'width 300ms ease',
                }}
              />
            </div>
          )}
        </div>
        <Link
        to="/billing#sms-bundles"
          data-testid="sms-usage-topup-link"
          style={{
            flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '6px 10px', borderRadius: 8,
            border: '1px solid var(--line, #e5e7eb)',
            background: 'var(--bg2, #fff)', color: 'var(--acc, #6366f1)',
            fontSize: 12, fontWeight: 600, textDecoration: 'none',
          }}
        >
          <Plus size={12} /> Top up SMS credits
        </Link>
      </div>
    </div>
  );
};
